import {Injectable} from '@angular/core';
import {BehaviorSubject, forkJoin, Observable, tap} from 'rxjs';
import {NotificationsApiService} from './api/notifications-api.service';
import {LocalStorage} from '../decorators/local-storage.decorator';
import {
  AccountBillingAlertsPaymentMethodExpires,
  DateTimeDto,
  GeneralAnnouncementDto,
  INotificationDto,
  NotificationSource,
  NotificationStatus,
  NotificationType,
  UserNotificationDto,
} from '../types/notifications.type';
import {NotificationsManager} from '../common/elf-store/notifications/notifications.manager';
import {UserInfoQuery} from '../common/elf-store/user-info/user-info.query';
import {TrialInfo, UserInfoState} from '../common/elf-store/user-info/user-info.type';
import {PluralPipe} from '../common/pipes/plural.pipe';
import {Severity} from '../types/severity';

const pluralize = new PluralPipe().transform;

@Injectable({
  providedIn: 'root',
})
export class NotificationsService {
  @LocalStorage('true') hasUnreadNotification: boolean;
  unreadNotifCounter = new BehaviorSubject(0);
  userInfo: UserInfoState;

  constructor(
    private userInfoQuery: UserInfoQuery,
    private notificationsApiService: NotificationsApiService,
    private notificationManager: NotificationsManager,
  ) {}

  static getSeverity(
    item: UserNotificationDto | GeneralAnnouncementDto,
    trialInfo: TrialInfo,
    isAgencyMode?: boolean,
  ): Severity {
    if ('type' in item) {
      if (item.type === NotificationType.AccountBillingAlertsNoCardTrialExpires) {
        if (NotificationsService.shouldShowTrialNoCardExpiredNotification(item, trialInfo)) {
          return 'danger';
        }

        return 'primary';
      }
    }

    if ('source' in item) {
      if (item.source === NotificationSource.AccountBillingAlerts || item.source === NotificationSource.AccountAlerts) {
        return isAgencyMode ? 'danger-agency' : 'danger';
      }
    }
    return isAgencyMode ? 'primary-agency' : 'primary';
  }

  static getBaseTextNotification(item: UserNotificationDto, trialInfo: TrialInfo) {
    if (NotificationsService.shouldShowTrialNoCardExpiredNotification(item, trialInfo)) {
      return 'Your free trial has expired!';
    }

    if (item.type === NotificationType.AccountBillingAlertsNoCardTrialExpires && 'expireDate' in item.payload) {
      const daysLeft = this.calculateDaysToExpire(item.payload.expireDate);
      return `You have <b>${daysLeft === 0 ? 'less than one' : daysLeft}</b> ${pluralize(daysLeft || 1, 'day', 'days')} on your free trial.`;
    }

    if ('paymentLink' in item.payload) {
      return 'Attention! Your credit card payment has failed. ';
    }
    if ('last4' in item.payload && 'expireDate' in item.payload) {
      if (this.calculateDaysToExpire(item.payload.expireDate) >= 1) {
        return 'Payment method is about to expire. ';
      } else {
        return 'Payment method has expired. ';
      }
    }

    if ('addedCount' in item.payload) {
      const totalCountItems = item.payload.addedCount + item.payload.duplicatedCount + item.payload.invalidCount;
      const itemLabel = pluralize(totalCountItems, 'item', 'items');
      return `${totalCountItems} ${itemLabel} have been processed: `;
    }
  }

  static getDetailsTextNotification({payload}: UserNotificationDto) {
    if ('paymentLink' in payload) {
      const showedSiteCount = 5;
      const siteList = payload.sites
        .slice(0, showedSiteCount)
        .map(el => ' ' + el.host)
        .toString();

      const restCountOfSites = payload.sites.length - showedSiteCount;

      const additionalSitesText =
        restCountOfSites >= 1 ? ` and ${restCountOfSites} ${pluralize(restCountOfSites, 'other', 'others')}` : '';

      return ` Site: ${siteList}${additionalSitesText}.`;
    }

    if ('last4' in payload) {
      const days = 'expireDate' in payload ? this.calculateDaysToExpire(payload.expireDate) : 0;
      if (days >= 1) {
        return `Your credit card ending in ${payload.last4} expires in ${days} ${pluralize(days, 'day', 'days')}.`;
      }
    }

    if ('addedCount' in payload) {
      const addedMessage = `${payload.addedCount} ${pluralize(payload.addedCount, 'site was added', 'sites were added')}`;
      const duplicatedMessage = `${payload.duplicatedCount} ${pluralize(payload.duplicatedCount, 'site existed before', 'sites existed before')}`;
      const invalidMessage = `${payload.invalidCount} ${pluralize(payload.invalidCount, 'URL is invalid', 'URLs are invalid')}`;

      return `${addedMessage}, ${duplicatedMessage}, ${invalidMessage}`;
    }
  }

  static calculateDaysToExpire(expireDate: DateTimeDto) {
    const currentDateMillis = Date.now();
    const dateDifference = expireDate.dateTimestamp - currentDateMillis;
    const convertToDays = 24 * 60 * 60 * 1000;
    return Math.floor(dateDifference / convertToDays);
  }

  static getNumberOfDaysToExpire(item: UserNotificationDto): number {
    const payload: AccountBillingAlertsPaymentMethodExpires = item.payload as AccountBillingAlertsPaymentMethodExpires;
    return this.calculateDaysToExpire(payload.expireDate);
  }

  static shouldShowTrialNoCardExpiredNotification(item: UserNotificationDto, trialInfo: TrialInfo): boolean {
    return (
      trialInfo.isTrialNoCardExpired ||
      (trialInfo.isTrialNoCard &&
        'expireDate' in item.payload &&
        NotificationsService.calculateDaysToExpire(item.payload.expireDate) < 0)
    );
  }

  getAllNotifications() {
    return this.userInfoQuery.userInfo$.pipe(
      tap((userInfo: UserInfoState) => {
        this.userInfo = userInfo;
        if (this.userInfo.ssoUserId) {
          this.notificationManager.setLoading(true);
          forkJoin({
            personalPayload: this.notificationsApiService.getPersonalNotifications(this.userInfo.ssoUserId),
            generalPayload: this.notificationsApiService.getGeneralAnnouncements({page: 0, limit: 3}),
          }).subscribe(
            ({
              personalPayload,
              generalPayload,
            }: {
              personalPayload: {payload: INotificationDto<UserNotificationDto>};
              generalPayload: {payload: INotificationDto<GeneralAnnouncementDto>};
            }) => {
              this.notificationManager.setPersonalNotifications(personalPayload.payload.thisPageItems);
              this.notificationManager.setGeneralAnnouncements(generalPayload.payload.thisPageItems);
              this.calculateUnReadNotifications(
                personalPayload.payload.thisPageItems,
                generalPayload.payload.thisPageItems,
              );
              this.notificationManager.setLoading(false);
            },
          );
        }
      }),
    );
  }

  readNotification(notificationId: string, status: string): Observable<UserInfoState> {
    return this.userInfoQuery.userInfo$.pipe(
      tap(({ssoUserId}: UserInfoState) => {
        this.notificationsApiService.readNotification(ssoUserId, {notificationId, status}).subscribe();
      }),
    );
  }

  readAllNotifications(ids: string[], status: string) {
    return this.userInfoQuery.userInfo$.pipe(
      tap(({ssoUserId}: UserInfoState) => {
        this.notificationsApiService.readAllNotifications(ssoUserId, {ids, status}).subscribe();
      }),
    );
  }

  updateNotifications(notifications: UserNotificationDto[]) {
    this.notificationManager.setPersonalNotifications([]);
    this.notificationManager.setPersonalNotifications(notifications);
  }

  calculateUnReadNotifications(personalNotifications: UserNotificationDto[], generalCount: GeneralAnnouncementDto[]) {
    const hasUnreadNotifications: boolean = this.hasUnreadNotification;

    const countPersonalUnread = personalNotifications.reduce((acc, notification) => {
      if (notification.status === NotificationStatus.NEW) {
        return acc + 1;
      } else {
        return acc;
      }
    }, 0);

    this.unreadNotifCounter.next(countPersonalUnread + (hasUnreadNotifications ? generalCount.length : 0));
  }

  clearUnreadNotifications() {
    this.hasUnreadNotification = false;
  }

  resetNotifications() {
    this.unreadNotifCounter.next(0);
    this.clearUnreadNotifications();
    this.notificationManager.reset();
  }

  isNoCardTrialNotification(notification: UserNotificationDto) {
    return notification.type === NotificationType.AccountBillingAlertsNoCardTrialExpires;
  }
}
